import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";

const SECTORS_RH = ["Third Man", "Point", "Cover", "Mid Off", "Mid On", "Mid Wicket", "Square Leg", "Fine Leg"];

export function runColor(r) {
  if (r >= 6) return "#D90416";
  if (r >= 4) return "#F59E0B";
  if (r >= 3) return "#7C3AED";
  if (r >= 1) return "#1E40AF";
  return "#94A3B8";
}

function sectorFromAngle(dx, dy) {
  // angle measured clockwise from top (12 o'clock)
  let ang = Math.atan2(dx, -dy); // 0 at top, positive clockwise
  if (ang < 0) ang += Math.PI * 2;
  return Math.floor((ang / (Math.PI * 2)) * 8) % 8;
}

export function WagonWheel({ shots = [], onAdd, hand = "Right-hand", readOnly = false, dismissalMode = false, size = 320 }) {
  const ref = useRef(null);
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState({ runs: 1, shot_type: "Drive", bowler_type: "Pace" });
  const isLH = hand?.startsWith("Left");

  const handleClick = (e) => {
    if (readOnly) return;
    const rect = ref.current.getBoundingClientRect();
    let nx = (e.clientX - rect.left) / rect.width;
    let ny = (e.clientY - rect.top) / rect.height;
    nx = Math.min(Math.max(nx, 0), 1);
    ny = Math.min(Math.max(ny, 0), 1);
    const dx = nx - 0.5, dy = ny - 0.5;
    const sector = sectorFromAngle(dx, dy);
    setPending({ x: Number(nx.toFixed(3)), y: Number(ny.toFixed(3)), sector });
    setForm({ runs: dismissalMode ? 0 : 1, shot_type: "Drive", bowler_type: "Pace" });
  };

  const confirm = () => {
    const sName = SECTORS_RH[isLH ? (8 - pending.sector) % 8 : pending.sector];
    onAdd({ ...pending, ...form, runs: Number(form.runs), sector_name: sName, is_dismissal: dismissalMode });
    setPending(null);
  };

  const cx = size / 2, cy = size / 2, R = size / 2 - 6;

  return (
    <div className="relative inline-block">
      <svg ref={ref} width={size} height={size} viewBox={`0 0 ${size} ${size}`} onClick={handleClick}
        data-testid="wagon-wheel-svg" className={readOnly ? "" : "cursor-crosshair"} style={{ touchAction: "none" }}>
        <defs>
          <radialGradient id="grass" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#166534" /><stop offset="100%" stopColor="#14532d" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={R} fill="url(#grass)" stroke="#052e16" strokeWidth="3" />
        <circle cx={cx} cy={cy} r={R * 0.55} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeDasharray="3 4" />
        {/* sector lines */}
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2 - Math.PI / 2 + Math.PI / 8;
          return <line key={i} x1={cx} y1={cy} x2={cx + R * Math.cos(a)} y2={cy + R * Math.sin(a)} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />;
        })}
        {/* pitch */}
        <rect x={cx - 8} y={cy - 26} width="16" height="52" rx="2" fill="#d6b98c" opacity="0.85" />
        {/* shots */}
        {shots.map((s, i) => (
          <g key={i}>
            <line x1={cx} y1={cy} x2={s.x * size} y2={s.y * size} stroke={runColor(s.runs)} strokeWidth={s.is_dismissal ? 0 : 1.5} opacity="0.55" />
            {s.is_dismissal
              ? <g><line x1={s.x * size - 5} y1={s.y * size - 5} x2={s.x * size + 5} y2={s.y * size + 5} stroke="#fff" strokeWidth="2.5" /><line x1={s.x * size + 5} y1={s.y * size - 5} x2={s.x * size - 5} y2={s.y * size + 5} stroke="#fff" strokeWidth="2.5" /></g>
              : <circle cx={s.x * size} cy={s.y * size} r={s.runs >= 4 ? 6 : 4} fill={runColor(s.runs)} stroke="#fff" strokeWidth="1.5" />}
          </g>
        ))}
        {pending && <circle cx={pending.x * size} cy={pending.y * size} r="6" fill="#fff" stroke="#D90416" strokeWidth="2" />}
      </svg>

      {pending && !readOnly && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg" data-testid="shot-summary-panel">
          <div className="bg-white rounded-xl p-4 w-64 shadow-2xl">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{SECTORS_RH[isLH ? (8 - pending.sector) % 8 : pending.sector]}</p>
            {!dismissalMode && (
              <>
                <label className="text-xs font-semibold">Runs</label>
                <div className="flex gap-1 my-1 flex-wrap">{[0, 1, 2, 3, 4, 6].map((r) => <button key={r} onClick={() => setForm({ ...form, runs: r })} data-testid={`shot-runs-${r}`} className={`w-9 h-9 rounded-lg font-bold text-sm ${form.runs === r ? "text-white" : "bg-slate-100"}`} style={form.runs === r ? { background: runColor(r) } : {}}>{r}</button>)}</div>
                <label className="text-xs font-semibold">Shot</label>
                <select className="w-full h-8 rounded border border-slate-200 text-sm mb-1" value={form.shot_type} onChange={(e) => setForm({ ...form, shot_type: e.target.value })}>{["Drive", "Cut", "Pull", "Flick", "Sweep", "Defence", "Loft", "Glance"].map((s) => <option key={s}>{s}</option>)}</select>
                <label className="text-xs font-semibold">Bowler</label>
                <select className="w-full h-8 rounded border border-slate-200 text-sm mb-2" value={form.bowler_type} onChange={(e) => setForm({ ...form, bowler_type: e.target.value })}>{["Pace", "Spin", "Medium"].map((s) => <option key={s}>{s}</option>)}</select>
              </>
            )}
            {dismissalMode && <p className="text-sm mb-2">Mark where you got out.</p>}
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="flex-1" onClick={() => setPending(null)}>Cancel</Button>
              <Button size="sm" className="flex-1 bg-brand hover:bg-brand-dark" onClick={confirm} data-testid="confirm-shot">Add</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
